/**
 * Localized DataGrid filter operators for events / sales / tickets.
 */
import { getNameFilterOperators } from '../components/filters/nameFilter';
import { getCityFilterOperators } from '../components/filters/cityFilter';
import { getEventTypeFilterOperators } from '../components/filters/eventTypeFilter';
import { getVenueFilterOperators } from '../components/filters/venueFilter';
import { getEventCategoryFilterOperators } from '../components/filters/eventCategoryFilter';
import { getStartDateFilterOperators } from '../components/filters/startDateFilter';
import { getIsActiveFilterOperators } from '../components/filters/isActiveFitler';
import { getSaleChannelOptions, getSaleStatusOptions } from './salesUtils';

const EVENT_FILTER_FACTORIES = {
  name: getNameFilterOperators,
  city: getCityFilterOperators,
  type: getEventTypeFilterOperators,
  venue: getVenueFilterOperators,
  category: getEventCategoryFilterOperators,
  startdate: getStartDateFilterOperators,
  isVerified: getIsActiveFilterOperators,
};

const looksLikeSaleStatusOptions = (options) => {
  if (!Array.isArray(options) || !options.length) return false;
  return options.some((o) => {
    const v = o && typeof o === 'object' ? o.value : o;
    return v === 0 || v === 1 || v === '0' || v === '1';
  });
};

const looksLikeChannelOptions = (options) => {
  if (!Array.isArray(options) || !options.length) return false;
  return options.some((o) => {
    const v = o && typeof o === 'object' ? String(o.value ?? '') : String(o ?? '');
    return /^(w|m|sp|sa|op|vp|a|ocm|vm)$/i.test(v) || /website|mobile/i.test(v);
  });
};

/**
 * @param {object} col DataGrid column
 * @param {Function} translate react-admin translate
 */
export const localizeGridColumnFilters = (col, translate) => {
  if (!col) return col;
  const t = translate;
  const next = { ...col };

  const factory = EVENT_FILTER_FACTORIES[col.field];
  if (factory) {
    next.filterOperators = factory(t);
  }

  if (col.field === 'channel' && looksLikeChannelOptions(col.valueOptions)) {
    next.valueOptions = getSaleChannelOptions(t);
    next.getOptionLabel = (opt) => {
      if (opt && typeof opt === 'object') return opt.label ?? String(opt.value ?? '');
      const found = next.valueOptions.find((o) => o.value === opt);
      return found?.label ?? String(opt ?? '');
    };
  }

  if (col.field === 'status' && looksLikeSaleStatusOptions(col.valueOptions)) {
    next.valueOptions = getSaleStatusOptions(t);
    next.getOptionLabel = (opt) => {
      if (opt && typeof opt === 'object') return opt.label ?? String(opt.value ?? '');
      const found = next.valueOptions.find((o) => String(o.value) === String(opt));
      return found?.label ?? String(opt ?? '');
    };
  }

  if (col.field === 'isUsed' && col.type === 'singleSelect') {
    next.valueOptions = [
      { value: 'true', label: t('tickets.usage_yes', { _: 'Yes' }) },
      { value: 'false', label: t('tickets.usage_no', { _: 'No' }) },
    ];
    next.getOptionLabel = (opt) => {
      if (opt && typeof opt === 'object') return opt.label ?? String(opt.value ?? '');
      const found = next.valueOptions.find((o) => String(o.value) === String(opt));
      return found?.label ?? String(opt ?? '');
    };
  }

  return next;
};
